/**
 * useFeed Hook
 *
 * DB-first posts feed (Posts Phase 1). Wraps postsService with state
 * management. Components consume this — never the service directly.
 *
 * Page 1 rides useCachedRead ('posts_feed_cache'): the last-known page paints
 * instantly, then the DB revalidates. Deeper pages live in tail state via
 * loadMore() (keyset cursor) and are never cached. There is NO realtime
 * subscription on posts (RLS subqueries break postgres_changes — migration
 * 019 comment); freshness is refetch on mount + visibilitychange + after
 * compose, via refresh().
 *
 * Likes fold in here — feed rows already carry like_count/viewer_liked.
 * toggleLike is optimistic: state + cache update first, DB write after,
 * revert + revalidate on failure.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  fetchFeedPage,
  likePost,
  unlikePost,
  FEED_PAGE_SIZE,
} from '../services/posts/postsService';
import { getProfileUserId } from '../utils/identityUtils';
import { isFullTier } from '../utils/userTier';
import useCachedRead from './useCachedRead';

const FEED_CACHE_KEY = 'posts_feed_cache';

const useFeed = () => {
  const profileUserId = getProfileUserId();

  const {
    data: page1,
    loaded,
    refreshing,
    error,
    refresh: revalidatePage1,
    mutate,
  } = useCachedRead(FEED_CACHE_KEY, () => fetchFeedPage(), {
    // Full-only: Posts is a Full feature — a Simple profile's bare
    // profile_user_id must not silently unlock the feed fetch.
    enabled: !!profileUserId && isFullTier(),
  });

  // Deeper pages — never cached; cleared whenever page 1 refreshes.
  const [tail, setTail] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  // Set when a page comes back short — no more posts below.
  const [exhausted, setExhausted] = useState(false);

  const posts = useMemo(() => {
    const merged = [...(page1 || []), ...tail];
    // A post created between page fetches shifts the keyset window — dedup by
    // id (first occurrence wins; page 1 is fresher than the tail).
    const seen = new Set();
    return merged.filter((p) => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }, [page1, tail]);

  const postsRef = useRef(posts);
  postsRef.current = posts;

  const hasMore = !exhausted && (page1?.length || 0) >= FEED_PAGE_SIZE;

  /**
   * Full refresh back to page 1 — used on visibilitychange and after compose.
   * Clears the tail so the keyset window can't go stale under a changed page 1.
   */
  const refresh = useCallback(() => {
    setTail([]);
    setExhausted(false);
    revalidatePage1();
  }, [revalidatePage1]);

  // Foreground catch-up: the feed has no realtime channel, so returning to the
  // app is one of the three freshness moments (mount / visibility / compose).
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [refresh]);

  /**
   * Load the next page below the last visible row (keyset cursor, both params
   * from the same row). Appends to tail state — never the cache.
   */
  const loadMore = useCallback(async () => {
    const current = postsRef.current;
    if (loadingMore || exhausted || current.length === 0) return;

    const last = current[current.length - 1];
    setLoadingMore(true);
    try {
      const rows = await fetchFeedPage({
        beforeCreatedAt: last.created_at,
        beforeId: last.id,
      });
      if (rows.length < FEED_PAGE_SIZE) setExhausted(true);
      setTail((t) => [...t, ...rows]);
    } catch (err) {
      // Tail fetch failure is non-destructive: what's on screen stays.
      console.error('[FEED] loadMore failed:', err?.message);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, exhausted]);

  /**
   * Like/unlike with optimistic count + heart. The update is applied to both
   * page 1 (mutate — state + cache together) and the tail — it only touches
   * the matching id, so the non-owning list is a no-op. On DB failure:
   * revert the optimistic change, then revalidate page 1 against the DB.
   */
  const toggleLike = useCallback(
    async (postId) => {
      const userId = getProfileUserId();
      const target = postsRef.current.find((p) => p.id === postId);
      if (!userId || !target) return false;

      const liked = !!target.viewer_liked;
      const applyLike = (rows, nowLiked) =>
        (rows || []).map((p) =>
          p.id === postId
            ? {
                ...p,
                viewer_liked: nowLiked,
                like_count: Number(p.like_count) + (nowLiked ? 1 : -1),
              }
            : p
        );

      const optimistic = (nowLiked) => {
        mutate((rows) => applyLike(rows, nowLiked));
        setTail((t) => applyLike(t, nowLiked));
      };

      optimistic(!liked);
      const ok = liked ? await unlikePost(postId, userId) : await likePost(postId, userId);
      if (!ok) {
        optimistic(liked); // revert
        revalidatePage1(); // resync page 1 with the DB
      }
      return ok;
    },
    [mutate, revalidatePage1]
  );

  return {
    posts,
    // Anonymous users have no feed — not "loading", just empty.
    loading: !!profileUserId && !loaded,
    loaded,
    refreshing,
    error,
    hasMore,
    loadingMore,
    refresh,
    loadMore,
    toggleLike,
  };
};

export default useFeed;
